/**
 * Qué se ve en la celda de producto de una línea de la orden.
 *
 * Existe por una razón concreta: los últimos tres bugs de esa celda fueron de
 * VISIBILIDAD, no de lógica de negocio.
 *
 *   1. Texto de lectura ("Lo que se leyó") en líneas cargadas a mano, donde no
 *      hay ningún documento del que hablar.
 *   2. El aviso de "líneas sin producto" en una orden nueva y vacía, que es la
 *      pantalla más común: el formulario arranca con una línea en blanco.
 *   3. Una caja de "Agregar al stock" que sobrevivió a dos rediseños y hablaba de
 *      un flujo que ya no existe.
 *
 * Las tres son frases verificables —"una línea manual NO debe mostrar esto"— y por
 * eso se verifican acá, en vez de mirando la pantalla, que es lo que no se puede
 * hacer desde este lado.
 */

export interface ItemLike {
  productId?: string;
  /** Lo que decía el documento. Su presencia es la marca de "vino de un documento". */
  description?: string;
  /** Candidatos que propuso el matching. También marca origen documental. */
  candidates?: unknown[];
  confidence?: 'high' | 'review';
  reviewed?: boolean;
}

/**
 * La línea vino de un documento leído.
 *
 * La marca es el texto leído o los candidatos: cualquiera de los dos existe solo si
 * hubo una lectura. Una línea cargada a mano no tiene ninguno.
 */
export function cameFromDocument(item: ItemLike): boolean {
  return Boolean(item.description?.trim() || (item.candidates?.length ?? 0) > 0);
}

/**
 * La celda muestra el bloque de lectura (texto + opciones) en vez del buscador.
 * Son excluyentes a propósito: dos caminos para elegir un producto es cómo se
 * llega a los restos que hubo que borrar.
 */
export function showsReadingBlock(item: ItemLike): boolean {
  return cameFromDocument(item);
}

/** Muestra el buscador de productos: la línea se carga a mano. */
export function showsProductSearch(item: ItemLike): boolean {
  return !cameFromDocument(item);
}

/**
 * La línea necesita una elección: vino de un documento y todavía no tiene producto.
 * Es lo único que hace aparecer la barra de pendientes y el aviso de guardado.
 */
export function needsProduct(item: ItemLike): boolean {
  return cameFromDocument(item) && !item.productId;
}

/** Cuántas líneas justifican la barra de pendientes. Cero = no aparece. */
export function pendingCount(items: ItemLike[]): number {
  return items.filter(needsProduct).length;
}

export type OptionTone = 'green' | 'amber' | 'grey';
/**
 * Semáforo de una opción: verde cuando explica todo lo que dice el documento (o
 * vino por código de barras), ámbar cuando hay algo que no explica, gris cuando
 * solo se parece.
 */
export function optionTone(candidate: {
  confidence?: 'high' | 'review';
  missingWords?: string[];
}): OptionTone {
  const dudoso = (candidate.missingWords?.length ?? 0) > 0;
  if (dudoso) return 'amber';
  return candidate.confidence === 'high' ? 'green' : 'grey';
}

/**
 * Líneas "a revisar" según el diseño viejo de preselección.
 *
 * Devuelve 0 siempre en el flujo actual: una línea importada nunca tiene producto
 * hasta que el usuario elige uno, y esta regla exige `productId`. La barra que la
 * usa es código muerto y está anotado acá para que no se "arregle" sin entender
 * por qué no aparece.
 */
export function reviewCount(items: ItemLike[]): number {
  return items.filter(
    (i) => Boolean(i.productId) && i.confidence === 'review' && !i.reviewed,
  ).length;
}

/**
 * Nombre con el que arranca el alta de un producto nuevo.
 *
 * Prefiere SIEMPRE lo que leyó el documento. Si se está creando un producto es
 * porque ningún candidato sirve, así que proponer el nombre de uno que se descartó
 * mete en el producto nuevo el nombre de otro artículo — y después queda en el
 * stock con un nombre que no es el suyo.
 *
 * La única excepción es una elección explícita del usuario (eligió un ítem del
 * catálogo para crearlo): ahí manda lo que eligió, no lo que se leyó.
 */
export function createNameSeed(
  item: { description?: string; matchName?: string },
  override?: { name?: string },
): string {
  return (override?.name || item.description?.trim() || item.matchName || '').trim();
}
